import {
	type DayClose,
	dateAnchor,
	deriveDayDogam,
	formatKstDate,
	type HouseholdConsumptionState,
	kstDateKey,
	resolveLivingBudgetPlan,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { navigate } from "./chrome";
import { DayStamp } from "./DayClose";
import { monthStartDayPref } from "./prefs";
export function DayDogam({
	receipts,
	household,
	closes,
	onCloseDay,
	onShare,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	closes: DayClose[];
	onCloseDay: (date: string) => void;
	onShare: (kind: "month_home" | "month_under", anchor: Date) => void;
}) {
	const [anchor, setAnchor] = useState(new Date());
	const [selected, setSelected] = useState("");
	const startDay = monthStartDayPref();
	const plan = resolveLivingBudgetPlan(household, anchor, startDay);
	const result = deriveDayDogam(
		receipts,
		plan,
		household.recurringPayments ?? [],
		closes,
		anchor,
	);
	const today = kstDateKey(new Date());
	const days = [];
	for (
		let t = result.period.start.getTime();
		t < result.period.end.getTime();
		t += 86400000
	)
		days.push(kstDateKey(new Date(t)));
	const firstWeekday = new Date(
		result.period.start.getTime() + 9 * 3600000,
	).getUTCDay();
	const offset = (firstWeekday + 6) % 7;
	const home = new Map<string, number>();
	for (const day of result.days)
		for (const id of day.close?.homeItemIds ?? [])
			home.set(id, (home.get(id) ?? 0) + 1);
	return (
		<section className="day-dogam">
			<div className="period-heading">
				<button
					type="button"
					className="quiet-button"
					aria-label="지난 달 보기"
					onClick={() => {
						setSelected("");
						setAnchor(new Date(result.period.start.getTime() - 1));
					}}
				>
					‹
				</button>
				<h2>
					{formatKstDate(result.period.start).replace(/ \(.*\)$/, "")} ~{" "}
					{formatKstDate(new Date(result.period.end.getTime() - 1)).replace(
						/ \(.*\)$/,
						"",
					)}
				</h2>
				<button
					type="button"
					className="quiet-button"
					aria-label="다음 달 보기"
					disabled={result.period.end > new Date()}
					onClick={() => {
						setSelected("");
						setAnchor(result.period.end);
					}}
				>
					›
				</button>
			</div>
			<p className="field-hint">
				이번 기간의 날이 모여요. 연속으로 찍지 않아도 쌓인 날은 그대로예요.
			</p>
			<fieldset className="day-calendar" aria-label="날 도감 달력">
				{["월", "화", "수", "목", "금", "토", "일"].map((d) => (
					<span key={d} className="weekday">
						{d}
					</span>
				))}
				{["월", "화", "수", "목", "금", "토", "일"]
					.slice(0, offset)
					.map((day) => (
						<span key={`space-${day}`} aria-hidden="true" />
					))}
				{days.map((date) => {
					const close = closes.find((c) => c.date === date);
					return (
						<button
							key={date}
							type="button"
							disabled={date > today}
							aria-pressed={selected === date}
							aria-label={`${formatKstDate(dateAnchor(date))}${close ? " 마감한 날" : " 기록 보기"}`}
							onClick={() => setSelected(date)}
						>
							<span>{Number(date.slice(-2))}</span>
							{close ? (
								<DayStamp kind={close.kind} small />
							) : (
								<span className="calendar-empty" aria-hidden="true">
									·
								</span>
							)}
						</button>
					);
				})}
			</fieldset>
			{selected ? (
				<div className="day-selected">
					<h3>{formatKstDate(dateAnchor(selected))}</h3>
					<p>
						{
							receipts.filter(
								(r) => !r.deletedAt && kstDateKey(r.transactedAt) === selected,
							).length
						}
						건의 기록
					</p>
					<button
						className="quiet-button"
						type="button"
						onClick={() => navigate(`/app/records?day=${selected}`)}
					>
						그날 내역 보기
					</button>
					<button
						className="button button-small"
						type="button"
						onClick={() => onCloseDay(selected)}
					>
						{closes.some((c) => c.date === selected)
							? "도장 보기·바꾸기"
							: "나중에 마감하기"}
					</button>
				</div>
			) : null}
			<dl className="day-counts">
				<div>
					<dt>
						적게 쓴 날 <small>0원 포함</small>
					</dt>
					<dd>{result.underDays}일</dd>
				</div>
				<div>
					<dt>0원 쓴 날</dt>
					<dd>{result.zeroDays}일</dd>
				</div>
				<div>
					<dt>집에 있는 걸로 버틴 날</dt>
					<dd>{result.homeDays}일</dd>
				</div>
			</dl>
			{home.size ? (
				<ul className="home-days">
					{[...home].map(([id, count]) => (
						<li key={id}>
							{household.items.find((item) => item.itemId === id)?.name ??
								"지금은 지운 물건"}
							<b>{count}일</b>
						</li>
					))}
				</ul>
			) : null}
			<div className="month-card-actions">
				<h3>이번 달 카드</h3>
				<button
					className="button"
					type="button"
					onClick={() => onShare("month_home", anchor)}
				>
					집에 있는 걸로 버틴 날
				</button>
				<button
					className="quiet-button"
					type="button"
					onClick={() => onShare("month_under", anchor)}
				>
					적게 쓴 날 카드
				</button>
			</div>
		</section>
	);
}
